// src/admin/AppDownloadManagement.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Save, Smartphone, X, Upload, Plus, Trash2, AlertCircle,
  Eye, Link as LinkIcon, CheckCircle, Image as ImageIcon, Zap, Palette
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const AppDownloadManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [uploadingMockup, setUploadingMockup] = useState(false);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  const defaultConfig = {
    active: true,
    heroTitle: 'Shop Organic Anytime, Anywhere',
    heroDescription: 'Download our app and get exclusive discounts, faster checkout, and track your orders in real-time.',

    playStoreUrl: 'https://play.google.com/store',
    playStoreActive: true,

    appStoreUrl: 'https://apps.apple.com',
    appStoreActive: true,

    qrCode: '',
    mockupImage: '',
    screenshots: [],

    // ✅ COLORS SECTION
    bgGradientFrom: '#F5AEC0',
    bgGradientTo: '#DC829A',
    titleColor: '#FFFFFF',
    descriptionColor: '#FFF2F6',
    badgeBg: 'rgba(255,255,255,0.2)',
    badgeText: '#FFFFFF',
    buttonTextColor: '#FFFFFF',
    learnMoreColor: '#FFF2F6',

    features: [
      { icon: 'zap', title: 'Fast Checkout', description: 'One-tap checkout with saved addresses' },
      { icon: 'gift', title: 'Exclusive Deals', description: 'App-only discounts and early access' },
      { icon: 'truck', title: 'Live Tracking', description: 'Track your orders in real-time' },
      { icon: 'bell', title: 'Instant Alerts', description: 'Get notified about order updates' },
    ],
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'app-download');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...defaultConfig, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'app-download'), config, { merge: true });
      setMessage({ type: 'success', text: 'App Download page updated!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('appDownloadUpdated'));
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file, folder, field, setUploadingState) => {
    setUploadingState(true);
    try {
      const url = await uploadToImageKit(file, folder);
      setConfig({ ...config, [field]: url });
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed.' });
    } finally {
      setUploadingState(false);
    }
  };

  const addScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingScreenshot(true);
    try {
      const url = await uploadToImageKit(file, 'app-screenshots');
      setConfig({ ...config, screenshots: [...(config.screenshots || []), url] });
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed.' });
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const removeScreenshot = (index) => {
    setConfig({
      ...config,
      screenshots: config.screenshots.filter((_, i) => i !== index),
    });
  };

  const addFeature = () => {
    setConfig({
      ...config,
      features: [...(config.features || []), { icon: 'check', title: '', description: '' }],
    });
  };

  const updateFeature = (index, field, value) => {
    const updated = [...config.features];
    updated[index] = { ...updated[index], [field]: value };
    setConfig({ ...config, features: updated });
  };

  const removeFeature = (index) => {
    setConfig({
      ...config,
      features: config.features.filter((_, i) => i !== index),
    });
  };

  const iconOptions = [
    { value: 'zap', label: '⚡ Zap' },
    { value: 'gift', label: '🎁 Gift' },
    { value: 'truck', label: '🚚 Truck' },
    { value: 'shield', label: '🛡️ Shield' },
    { value: 'heart', label: '❤️ Heart' },
    { value: 'bell', label: '🔔 Bell' },
    { value: 'lock', label: '🔒 Lock' },
    { value: 'refresh', label: '🔄 Refresh' },
    { value: 'trending', label: '📈 Trending' },
    { value: 'users', label: '👥 Users' },
    { value: 'award', label: '🏆 Award' },
    { value: 'download', label: '⬇️ Download' },
  ];

  const ColorInput = ({ label, field, description, allowRgba = false }) => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type={allowRgba ? 'text' : 'color'}
          value={config[field] || '#FFFFFF'}
          onChange={(e) => setConfig({ ...config, [field]: e.target.value })}
          className={allowRgba
            ? "flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono"
            : "w-14 h-11 rounded-lg border-2 border-gray-300 cursor-pointer"}
        />
        {!allowRgba && (
          <input
            type="text"
            value={config[field] || ''}
            onChange={(e) => setConfig({ ...config, [field]: e.target.value })}
            className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono uppercase"
          />
        )}
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Smartphone className="text-pink-500" size={26} /> App Download Page
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Mobile app ka download page aur colors yahan se control karein.
          </p>
        </div>

        <a href="/app-download" target="_blank" rel="noopener noreferrer"
          className="bg-pink-50 hover:bg-pink-100 text-pink-700 px-5 py-3 rounded-lg transition flex items-center gap-2 font-semibold border border-pink-200">
          <Eye size={18} /> Preview Page
        </a>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* STATUS */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Zap size={20} className="text-pink-500" /> Page Visibility
          </h2>
          <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-pink-50 border border-pink-200">
            <input type="checkbox" checked={config.active}
              onChange={(e) => setConfig({ ...config, active: e.target.checked })}
              className="w-5 h-5 accent-pink-500" />
            <div>
              <span className="text-sm font-semibold text-gray-800 block">
                Show App Download Page on Website
              </span>
              <span className="text-xs text-gray-600">
                Uncheck karne par section hide ho jayega
              </span>
            </div>
          </label>
        </div>

        {/* ✅ COLORS SECTION */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Palette size={20} className="text-pink-500" /> Colors
          </h2>

          <div className="space-y-6">

            {/* Background Gradient */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Background Gradient</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorInput
                  label="Gradient Start Color"
                  field="bgGradientFrom"
                  description="Left side color"
                />
                <ColorInput
                  label="Gradient End Color"
                  field="bgGradientTo"
                  description="Right side color"
                />
              </div>

              {/* Gradient Preview */}
              <div className="mt-3 h-16 rounded-lg shadow-inner"
                style={{
                  background: `linear-gradient(135deg, ${config.bgGradientFrom || '#F5AEC0'} 0%, ${config.bgGradientTo || '#DC829A'} 100%)`
                }}
              ></div>
              <p className="text-xs text-gray-500 mt-1">Live gradient preview</p>
            </div>

            {/* Text Colors */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Text Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorInput
                  label="Title Color"
                  field="titleColor"
                  description="Hero title text"
                />
                <ColorInput
                  label="Description Color"
                  field="descriptionColor"
                  description="Hero description text"
                />
                <ColorInput
                  label="Learn More Link Color"
                  field="learnMoreColor"
                  description="Learn more link"
                />
              </div>
            </div>

            {/* Badge */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Badge (Mobile App Label)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorInput
                  label="Badge Background"
                  field="badgeBg"
                  description="Use rgba for transparency (e.g., rgba(255,255,255,0.2))"
                  allowRgba={true}
                />
                <ColorInput
                  label="Badge Text Color"
                  field="badgeText"
                  description="Badge text"
                />
              </div>
            </div>

          </div>
        </div>

        {/* HERO CONTENT */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Smartphone size={20} className="text-pink-500" /> Hero Content
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Title</label>
              <input type="text" value={config.heroTitle}
                onChange={(e) => setConfig({ ...config, heroTitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-pink-500 text-sm"
                placeholder="Shop Organic Anytime, Anywhere" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Description</label>
              <textarea value={config.heroDescription}
                onChange={(e) => setConfig({ ...config, heroDescription: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-pink-500 text-sm resize-none" />
            </div>
          </div>
        </div>

        {/* STORE LINKS */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <LinkIcon size={20} className="text-pink-500" /> App Store Links
          </h2>

          <div className="space-y-5">
            <div className="p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">🤖 Google Play Store</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.playStoreActive !== false}
                    onChange={(e) => setConfig({ ...config, playStoreActive: e.target.checked })}
                    className="w-4 h-4 accent-pink-500" />
                  <span className="text-xs font-semibold text-gray-700">Show</span>
                </label>
              </div>
              <input type="url" value={config.playStoreUrl || ''}
                onChange={(e) => setConfig({ ...config, playStoreUrl: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:border-pink-500 text-sm"
                placeholder="https://play.google.com/store/apps/details?id=..." />
            </div>

            <div className="p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">🍎 Apple App Store</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.appStoreActive !== false}
                    onChange={(e) => setConfig({ ...config, appStoreActive: e.target.checked })}
                    className="w-4 h-4 accent-pink-500" />
                  <span className="text-xs font-semibold text-gray-700">Show</span>
                </label>
              </div>
              <input type="url" value={config.appStoreUrl || ''}
                onChange={(e) => setConfig({ ...config, appStoreUrl: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:border-pink-500 text-sm"
                placeholder="https://apps.apple.com/app/id..." />
            </div>
          </div>
        </div>

        {/* IMAGES */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <ImageIcon size={20} className="text-pink-500" /> Images
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Mockup Image</label>
              {config.mockupImage ? (
                <div className="relative">
                  <img src={config.mockupImage} alt="Mockup" className="w-full h-56 object-cover rounded-lg" />
                  <button type="button"
                    onClick={() => setConfig({ ...config, mockupImage: '' })}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition">
                  {uploadingMockup ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-400 border-t-transparent"></div>
                  ) : (
                    <>
                      <Upload className="text-gray-400 mb-2" size={28} />
                      <p className="text-sm font-semibold text-gray-600">Upload mockup</p>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingMockup}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) uploadImage(file, 'app-mockup', 'mockupImage', setUploadingMockup);
                    }} />
                </label>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">QR Code (Optional)</label>
              {config.qrCode ? (
                <div className="relative">
                  <img src={config.qrCode} alt="QR" className="w-full h-56 object-contain bg-white rounded-lg border" />
                  <button type="button"
                    onClick={() => setConfig({ ...config, qrCode: '' })}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition">
                  {uploadingQR ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-400 border-t-transparent"></div>
                  ) : (
                    <>
                      <Upload className="text-gray-400 mb-2" size={28} />
                      <p className="text-sm font-semibold text-gray-600">Upload QR Code</p>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingQR}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) uploadImage(file, 'app-qrcode', 'qrCode', setUploadingQR);
                    }} />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* FEATURES */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">Features (Why Download)</h2>
            <button type="button" onClick={addFeature}
              className="text-sm font-semibold text-pink-600 hover:text-pink-700 flex items-center gap-1">
              <Plus size={14} /> Add Feature
            </button>
          </div>

          <div className="space-y-3">
            {(config.features || []).map((feature, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg">
                <select value={feature.icon}
                  onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                  className="col-span-3 border border-gray-300 rounded-lg py-2 px-2 text-xs focus:outline-none focus:border-pink-500">
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input type="text" value={feature.title}
                  onChange={(e) => updateFeature(index, 'title', e.target.value)}
                  className="col-span-3 border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-pink-500"
                  placeholder="Title" />
                <input type="text" value={feature.description}
                  onChange={(e) => updateFeature(index, 'description', e.target.value)}
                  className="col-span-5 border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-pink-500"
                  placeholder="Description" />
                <button type="button" onClick={() => removeFeature(index)}
                  className="col-span-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* SCREENSHOTS */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">App Screenshots</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {(config.screenshots || []).map((shot, i) => (
              <div key={i} className="relative">
                <img src={shot} alt={`Screenshot ${i + 1}`}
                  className="w-full aspect-[9/19] object-cover rounded-lg border" />
                <button type="button" onClick={() => removeScreenshot(i)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                  <X size={12} />
                </button>
              </div>
            ))}

            <label className="flex flex-col items-center justify-center aspect-[9/19] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition">
              {uploadingScreenshot ? (
                <div className="animate-spin rounded-full h-6 w-6 border-4 border-pink-400 border-t-transparent"></div>
              ) : (
                <>
                  <Plus className="text-gray-400 mb-1" size={24} />
                  <p className="text-[10px] font-semibold text-gray-600 text-center px-1">Add Screenshot</p>
                </>
              )}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingScreenshot}
                onChange={addScreenshot} />
            </label>
          </div>
        </div>

        {/* SAVE */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`text-white py-3 px-8 rounded-lg font-semibold transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: '#F5AEC0' }}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="App Download page settings saved!"
        buttonText="Great!"
      />

    </div>
  );
};

export default AppDownloadManagement;